import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listApprovedPhotos,
  toggleLike,
  hasLiked,
  addComment,
  listComments,
  markView,
} from '@/lib/api';
import { getDeviceId } from '@/lib/device';
import type { Photo, Comment } from '@/types';

export function useGallery() {
  return useQuery({
    queryKey: ['gallery', 'larisa-si-dani'],
    queryFn: listApprovedPhotos,
    staleTime: 30 * 1000,
  });
}

export function useLike(photoId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!photoId) throw new Error('no photoId');
      return toggleLike(photoId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gallery'] });
      if (photoId) {
        qc.invalidateQueries({ queryKey: ['liked', photoId] });
      }
    },
  });
}

export function useComment(photoId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ author, body }: { author: string; body: string }) => {
      if (!photoId) throw new Error('no photoId');
      return addComment(photoId, author, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gallery'] });
      if (photoId) {
        qc.invalidateQueries({ queryKey: ['comments', photoId] });
      }
    },
  });
}

export async function recordView(photoId: string | undefined) {
  if (!photoId) return;
  try {
    await markView(photoId);
  } catch {}
}

export function useComments(photoId: string | undefined) {
  return useQuery({
    queryKey: ['comments', photoId],
    queryFn: () => listComments(photoId!),
    enabled: !!photoId,
  });
}

export function useHasLiked(photoId: string | undefined) {
  return useQuery({
    queryKey: ['liked', photoId, getDeviceId()],
    queryFn: () => hasLiked(photoId!),
    enabled: !!photoId,
  });
}

export function useView(photoId: string | undefined) {
  return useQuery({
    queryKey: ['view', photoId, getDeviceId()],
    queryFn: async () => {
      if (!photoId) return false;
      await markView(photoId);
      return true;
    },
    enabled: !!photoId,
    staleTime: Infinity,
  });
}

export type { Photo, Comment };
